import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/admin/StatusPill";
import { toast } from "sonner";
import { PlusCircle, Newspaper, CalendarClock, ArrowRight } from "lucide-react";

export default function AdminDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/dashboard");
      setData(r.data);
    } catch (e) {
      toast.error("Could not load dashboard");
    } finally { setLoading(false); }
  };

  const createIssue = async () => {
    try {
      const r = await api.post("/admin/issues", {});
      window.location.href = `/admin/issues/${r.data.id}`;
    } catch (e) {
      toast.error("Could not create issue", { description: e?.response?.data?.detail || "" });
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between">
        <div>
          <div className="kicker">Dashboard</div>
          <h1 className="mt-1 font-display text-3xl font-bold">This week</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={createIssue} data-testid="admin-dashboard-create-issue" className="bg-[color:var(--coh-blue)] text-white hover:bg-[color:var(--coh-blue)]/90 rounded-full">
            <PlusCircle className="h-4 w-4 mr-2" /> New issue
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {[1,2,3].map(i => <div key={i} className="h-32 rounded-xl bg-secondary animate-pulse" />)}
        </div>
      ) : (
        <>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <Card className="p-6">
              <div className="kicker">Human count</div>
              <div className="mt-2 font-display text-4xl font-bold text-gradient-brand" data-testid="admin-dashboard-human-count">{new Intl.NumberFormat().format(data.human_count_total || 0)}</div>
              <p className="mt-1 text-xs text-muted-foreground">All-time reconnections (web + Discord)</p>
            </Card>
            <Card className="p-6">
              <div className="kicker">Drafts</div>
              <div className="mt-2 font-display text-4xl font-bold">{data.drafts_needing_review}</div>
              <p className="mt-1 text-xs text-muted-foreground">In draft or in review</p>
            </Card>
            <Card className="p-6" data-testid="admin-dashboard-automation-card">
              <div className="flex items-center justify-between">
                <div className="kicker inline-flex items-center gap-2"><CalendarClock className="h-3.5 w-3.5" /> Automation</div>
                {data.automation?.is_running && (
                  <span className="text-[11px] font-medium text-[color:var(--coh-blue)] animate-pulse">Publishing…</span>
                )}
              </div>
              <div className={`mt-2 font-display text-4xl font-bold ${data.automation?.enabled ? "text-[color:var(--coh-success)]" : "text-muted-foreground"}`} data-testid="admin-dashboard-automation-state">
                {data.automation?.enabled ? "On" : "Paused"}
              </div>
              <p className="mt-1 text-xs text-muted-foreground" data-testid="admin-dashboard-automation-next">
                {data.automation?.enabled
                  ? (data.automation?.pending_catch_up ? "Catching up — next issue starts within a minute" : `Next issue: ${data.automation?.next_run_local || "—"}`)
                  : "Weekly auto-publish is off"}
              </p>
              <Link to="/admin/automation" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[color:var(--coh-blue)] hover:underline underline-offset-4" data-testid="admin-dashboard-automation-link">
                Manage schedule <ArrowRight className="h-3 w-3" />
              </Link>
            </Card>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div className="kicker">Current draft</div>
                {data.latest_draft && <StatusPill status={data.latest_draft.status} />}
              </div>
              {data.latest_draft ? (
                <>
                  <h3 className="mt-3 font-display text-2xl font-bold">{data.latest_draft.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">Issue #{data.latest_draft.issue_number} · {data.latest_draft.theme || "No theme"}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button asChild size="sm" variant="outline">
                      <Link to={`/admin/issues/${data.latest_draft.id}`}>Continue editing</Link>
                    </Button>
                  </div>
                </>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">No drafts yet. Create one to get started.</p>
              )}
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div className="kicker">Latest published</div>
                {data.latest_published && <StatusPill status={data.latest_published.status} />}
              </div>
              {data.latest_published ? (
                <>
                  <h3 className="mt-3 font-display text-2xl font-bold">{data.latest_published.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">Issue #{data.latest_published.issue_number}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button asChild size="sm" variant="outline">
                      <Link to={`/issues/${data.latest_published.slug}`} target="_blank">Open public page</Link>
                    </Button>
                    <Button asChild size="sm" variant="outline">
                      <Link to={`/admin/issues/${data.latest_published.id}`}>Open in editor</Link>
                    </Button>
                  </div>
                </>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">Nothing published yet.</p>
              )}
            </Card>
          </div>

          <div className="mt-6 grid gap-6">
            <Card className="p-6">
              <div className="flex items-center gap-2 kicker"><Newspaper className="h-3.5 w-3.5" /> Recent activity</div>
              <ul className="mt-4 divide-y divide-border">
                {(data.recent_audit || []).length === 0 && (
                  <li className="py-3 text-sm text-muted-foreground">No activity yet.</li>
                )}
                {(data.recent_audit || []).map((a) => (
                  <li key={a.id} className="py-3 flex items-start justify-between text-sm">
                    <div>
                      <div className="font-medium">{a.action}</div>
                      <div className="text-xs text-muted-foreground">{a.table_name}{a.record_id ? " · " + a.record_id.slice(0,8) : ""}</div>
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
